import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { FileUtil } from '@univacity/shared/util';
import {
  BUCKET,
  File as IFile,
  FindOneFile,
  IMAGE_FORMAT,
} from '@univacity/shared/types';
import { map, Observable, of } from 'rxjs';
import { Configuration } from '../config/configuration';

@Injectable({
  providedIn: 'root',
})
export class BucketService {
  private baseUrl: string;

  constructor(private http: HttpClient, private config: Configuration) {
    this.baseUrl = this.config.apiMediaUrl + '/bucket';
  }

  uploadOne(
    file: File,
    bucket: BUCKET = BUCKET.private
  ): Observable<HttpEvent<IFile | HttpEvent<IFile>>> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<IFile | HttpEvent<IFile>>(this.baseUrl, body, {
      params: { bucket },
      reportProgress: true,
      observe: 'events',
    });
  }

  uploadMultiple(
    files: FileList,
    bucket: BUCKET = BUCKET.private
  ): Observable<HttpEvent<IFile[] | HttpEvent<IFile[]>>> {
    const body = new FormData();
    for (const file of files as unknown as Array<string>) {
      body.append('files', file);
    }
    return this.http.post<IFile[]>(`${this.baseUrl}/multi`, body, {
      params: { bucket },
      reportProgress: true,
      observe: 'events',
    });
  }

  update(id: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    return this.http.put<IFile | HttpEvent<IFile>>(
      `${this.baseUrl}/${id}`,
      body,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  fetchFileUrl(
    fileSrc: string,
    query: FindOneFile = { timeout: 3000 }
  ): Observable<{ url: string }> {
    const src = fileSrc || '';
    const params = new HttpParams({ fromObject: query as Params });
    const slash = src.startsWith('/') ? '' : '/';
    const bucket = FileUtil.getBuketName(src);
    if (
      bucket === BUCKET.public &&
      Object.values(IMAGE_FORMAT).some((f) => src.endsWith(f))
    )
      return of({ url: this.getFullUrl(src, query) });
    return this.http
      .get<{ url: string }>(`${this.baseUrl}${slash}${src}/presign`, {
        params,
      })
      .pipe(
        map(({ url }) => {
          const params = new URLSearchParams(query as Params).toString();
          let delima = url.includes('?') ? '&' : '?';
          if (!params) delima = '';
          const u = Object.values(IMAGE_FORMAT).some((f) => fileSrc.endsWith(f))
            ? `${url}${delima}${params}`
            : url;
          return {
            url: u,
          };
        })
      );
  }

  getFullUrl(fileSrc: string, query: FindOneFile = {}) {
    const slash = fileSrc.startsWith('/') ? '' : '/';
    return `${this.baseUrl}${slash}${fileSrc}?${new URLSearchParams(
      query as Params
    ).toString()}`;
  }

  delete(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }
}
